import { createStore } from '../core/state/store';
import { icon, type IconName } from './icons';
import { t, tOr } from './i18n/i18n';
import { fetchBoosts, type BoostParams, type BoostType, type BoostView } from '../screens/shop/shop-api';

/**
 * Boosts bought from special offers: their wording, icons, countdown formatting, and the small store the
 * HUD icons (colony header, world map) read from. Everything a boost "says" about itself — the card on
 * the shop, the banner, the HUD tooltip — is built here from the same numbers the server froze on the row.
 */

export const BOOST_ICON: Record<BoostType, IconName> = {
  BUILD_SPEED: 'hammer',
  TRAIN_SPEED: 'troops',
  MINING: 'pickaxe',
  DEFENCE: 'shield',
  ATTACK: 'crosshair',
  RESEARCH: 'research',
};

export function resourceName(resource: string | undefined): string {
  return resource ? tOr(`term.resource.${resource}`, resource) : '';
}

export function boostTitle(type: BoostType, params: BoostParams): string {
  return t(`boost.${type}.title`, { resource: resourceName(params.resource) });
}

/** What the boost does, one fact per line (the tooltip body and the shop card's description). */
export function boostLines(type: BoostType, params: BoostParams, usesRemaining?: number | null): string[] {
  const n = (v: number | undefined) => String(Math.abs(v ?? 0));
  switch (type) {
    case 'BUILD_SPEED':
    case 'TRAIN_SPEED':
      return [t(`boost.${type}.line`, { pct: n(params.pct) })];
    case 'DEFENCE':
      return [t('boost.DEFENCE.line', { pct: n(params.pct) })];
    case 'ATTACK':
      return [t('boost.ATTACK.line', { pct: n(params.pct) }), ...(usesRemaining != null ? [t('boost.ATTACK.uses', { n: String(usesRemaining) })] : [])];
    case 'RESEARCH':
      return [t('boost.RESEARCH.speed', { pct: n(params.speedPct) }), t('boost.RESEARCH.cost', { pct: n(params.costPct) })];
    case 'MINING':
      return [
        t('boost.MINING.line', { pct: n(params.pct), resource: resourceName(params.resource) }),
        ...(params.otherPct ? [t('boost.MINING.other', { pct: n(params.otherPct) })] : []),
      ];
  }
}

/** Hours while there are at least 60 minutes left, then minutes (never "0m": a live boost shows at least 1m). */
export function formatBoostTime(ms: number): string {
  const minutes = Math.max(1, Math.ceil(ms / 60_000));
  if (minutes >= 60) return `${Math.floor(minutes / 60)}h`;
  return `${minutes}m`;
}

/** HH:MM:SS for the offers reset timer. */
export function formatClock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(Math.floor(total / 3600))}:${pad(Math.floor((total % 3600) / 60))}:${pad(total % 60)}`;
}

/** "499" -> "4.99": the EUR reference the Delta-V price is pegged to. Used only for the small "≈" hint. */
export function dvToEur(dv: number): string {
  return (dv / 100).toFixed(2);
}

export function boostIconHtml(type: BoostType, className = ''): string {
  return icon(BOOST_ICON[type], className);
}

// ─── the HUD store ─────────────────────────────────────────────────────────────────────────────────────
export const boostStore = createStore<BoostView[]>([]);
const REFRESH_MS = 60_000;
let timer: number | null = null;
let listener: (() => void) | null = null;

/** Live boosts only — a boost whose end passed between refreshes drops out immediately. */
export function liveBoosts(boosts: BoostView[], now: number = Date.now()): BoostView[] {
  return boosts.filter((b) => new Date(b.endsAt).getTime() > now && (b.usesRemaining === null || b.usesRemaining > 0));
}

export async function refreshBoosts(): Promise<void> {
  try {
    boostStore.set(await fetchBoosts());
  } catch {
    // Keep what we had — a failed poll must not blank the HUD.
  }
}

/** Starts polling and listening for `boosts:refresh` (fired after any purchase). Returns the teardown. */
export function startBoostPolling(): () => void {
  void refreshBoosts();
  timer = window.setInterval(() => void refreshBoosts(), REFRESH_MS);
  listener = () => void refreshBoosts();
  window.addEventListener('boosts:refresh', listener);
  return () => {
    if (timer !== null) window.clearInterval(timer);
    if (listener) window.removeEventListener('boosts:refresh', listener);
    timer = null;
    listener = null;
    boostStore.set([]);
  };
}
